import black from "../assets/cars/black.png";
import blue from "../assets/cars/blue.png";
import burgundy from "../assets/cars/burgundy.png";
import darkGray from "../assets/cars/dark-gray.png";
import gray from "../assets/cars/gray.png";
import green from "../assets/cars/green.png";
import orange from "../assets/cars/orange.png";
import red from "../assets/cars/red.png";
import taxiRemis from "../assets/cars/taxi-remis.png";
import white from "../assets/cars/white.png";
import yellow from "../assets/cars/yellow.png";

const CAR_IMAGES = {
  black,
  blue,
  burgundy,
  "dark-gray": darkGray,
  gray,
  green,
  orange,
  red,
  "taxi-remis": taxiRemis,
  white,
  yellow,
} as const;

type CarColorKey = keyof typeof CAR_IMAGES;

// Color representativo de cada ilustración, para resolver hex arbitrarios por cercanía.
const COLOR_RGB: Record<Exclude<CarColorKey, "taxi-remis">, [number, number, number]> = {
  black: [20, 20, 20],
  white: [255, 255, 255],
  gray: [160, 160, 160],
  "dark-gray": [75, 75, 75],
  blue: [40, 90, 200],
  red: [210, 45, 45],
  green: [45, 160, 80],
  orange: [235, 140, 30],
  yellow: [245, 205, 40],
  burgundy: [115, 30, 50],
};

// Nombres (ES/EN) a key de imagen.
const NAME_MAP: Record<string, CarColorKey> = {
  blanco: "white",
  white: "white",
  negro: "black",
  black: "black",
  gris: "gray",
  grisclaro: "gray",
  plateado: "gray",
  plata: "gray",
  silver: "gray",
  gray: "gray",
  grey: "gray",
  grisoscuro: "dark-gray",
  "dark-gray": "dark-gray",
  azul: "blue",
  blue: "blue",
  rojo: "red",
  red: "red",
  verde: "green",
  green: "green",
  naranja: "orange",
  naranjo: "orange",
  orange: "orange",
  amarillo: "yellow",
  yellow: "yellow",
  bordo: "burgundy",
  "bordó": "burgundy",
  borgona: "burgundy",
  "borgoña": "burgundy",
  vinotinto: "burgundy",
  burgundy: "burgundy",
  taxi: "taxi-remis",
  remis: "taxi-remis",
};

const hexToRgb = (value: string): [number, number, number] | null => {
  let hex = value.replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (!/^[0-9a-f]{6}$/i.test(hex)) return null;
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ];
};

const nearestColorKey = (rgb: [number, number, number]): CarColorKey => {
  let best: CarColorKey = "gray";
  let bestDist = Infinity;
  (Object.keys(COLOR_RGB) as Array<keyof typeof COLOR_RGB>).forEach((key) => {
    const [r, g, b] = COLOR_RGB[key];
    const dist =
      (r - rgb[0]) ** 2 + (g - rgb[1]) ** 2 + (b - rgb[2]) ** 2;
    if (dist < bestDist) {
      bestDist = dist;
      best = key;
    }
  });
  return best;
};

/**
 * Devuelve la imagen del auto que corresponde al color del vehículo.
 * Acepta nombres (ES/EN) o valores hex; para hex desconocidos elige el más cercano.
 */
export const getCarImage = (color?: string): string | undefined => {
  if (!color) return undefined;
  const normalized = color.trim().toLowerCase();

  const byName = NAME_MAP[normalized.replace(/\s+/g, "")];
  if (byName) return CAR_IMAGES[byName];

  const rgb = hexToRgb(normalized);
  if (rgb) return CAR_IMAGES[nearestColorKey(rgb)];

  return undefined;
};
